import { formatInTimeZone } from 'date-fns-tz';
import { parseISO } from 'date-fns';

/**
 * Get the user's current timezone
 */
export const getUserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

/**
 * Format a time string with timezone information
 * @param time - Time string (e.g., "14:30")
 * @param date - Date string (e.g., "2024-03-15")
 * @param timezone - IANA timezone string (e.g., "America/New_York")
 * @param format - Format string for output
 */
export const formatTimeWithZone = (
  time: string,
  date: string,
  timezone: string,
  format: string = 'h:mm a'
): string => {
  try {
    const dateTimeString = `${date}T${time}:00`;
    const dateTime = parseISO(dateTimeString);
    return formatInTimeZone(dateTime, timezone, format);
  } catch (error) {
    console.error('Error formatting time with zone:', error);
    return time;
  }
};

/**
 * Get timezone abbreviation (e.g., "CST", "EST")
 */
export const getTimezoneAbbreviation = (timezone: string, date?: Date): string => {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short'
    });
    const parts = formatter.formatToParts(date || new Date());
    const timeZonePart = parts.find(part => part.type === 'timeZoneName');
    return timeZonePart?.value || timezone;
  } catch (error) {
    console.error('Error getting timezone abbreviation:', error);
    return timezone;
  }
};

/**
 * Format time in both user's timezone and CST
 */
export const formatTimeInBothZones = (
  time: string,
  date: string,
  userTimezone: string
): string => {
  const cst = 'America/Chicago';
  const userTime = formatTimeWithZone(time, date, userTimezone);
  const userAbbr = getTimezoneAbbreviation(userTimezone);
  
  // If user is already in CST, don't show twice
  if (userTimezone === cst || userAbbr === getTimezoneAbbreviation(cst)) {
    return `${userTime} ${userAbbr}`;
  }
  
  const cstTime = formatTimeWithZone(time, date, cst);
  const cstAbbr = getTimezoneAbbreviation(cst);
  
  return `${userTime} ${userAbbr} (${cstTime} ${cstAbbr})`;
};
